'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/contexts/ThemeContext'
import { isTauriDesktop } from '@/lib/desktop/desktopUtils'
import { Capacitor } from '@capacitor/core'

interface PublicBranding {
  logo_url: string;
  primary_color: string;
  secondary_color: string;
  header_color: string | null;
  hero1_url: string | null;
  hero2_url: string | null;
  hero3_url: string | null;
  favicon_url: string | null;
}

export default function Footer() {
  const { isAuthenticated } = useAuth()
  const { currentLogo } = useTheme()
  const pathname = usePathname()
  const isClientZone = pathname?.startsWith('/client-zone') || (pathname === '/' && isAuthenticated)
  const [mounted, setMounted] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)
  const [branding, setBranding] = useState<PublicBranding | null>(null)

  const loadBranding = async () => {
    try {
      const response = await fetch('/api/v1/public-branding')
      const result = await response.json()
      if (result.success) {
        setBranding(result.data)
      }
    } catch (error: any) {
      // Suppress offline errors - they're expected and handled by fetch patch
      if (!error?.isOfflineError) {
        console.error('Error loading branding:', error)
      }
    }
  }

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setMounted(true)
      setIsDesktop(isTauriDesktop())
      loadBranding()
    })
    return () => cancelAnimationFrame(frame)
  }, [])

  // Hide footer in client zone (they have ClientZoneFooter)
  if (isClientZone) {
    return null
  }

  // Hide footer in desktop app (not needed)
  if (isDesktop) {
    return null
  }

  // Hide footer in Capacitor native app (not needed for mobile)
  // Use Capacitor.isNativePlatform() to properly detect native apps vs web
  if (Capacitor.isNativePlatform()) {
    return null
  }

  // Check if this is a white-label domain
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const hostnameWithoutPort = hostname.split(':')[0]
  const isWhitelabel = 
    !hostnameWithoutPort.includes('localhost') &&
    !hostnameWithoutPort.includes('127.0.0.1') &&
    hostnameWithoutPort !== 'dev-next.totalaccess.co.za' &&
    hostnameWithoutPort !== 'dev-staging.totalaccess.co.za' &&
    hostnameWithoutPort !== 'totalaccess.co.za' &&
    hostnameWithoutPort !== 'totalaccess.co.za'

  // Show minimal footer for white-label domains
  if (isWhitelabel && mounted) {
    return (
      <footer 
        className="footer py-6"
        style={{ backgroundColor: branding?.header_color || '#1e293b' }}
      >
        <div className="container mx-auto px-4 text-center text-sm text-white">
          <p>&copy; {new Date().getFullYear()} Total Access Software Solutions T/A Total Access Solutions. All rights reserved.</p>
        </div>
      </footer>
    )
  }

  // Footer logo and text color from client_id 1 branding
  const footerLogo = branding?.logo_url || currentLogo
  const footerBgColor = branding?.header_color || '#1e293b'
  const footerTextColor = branding?.secondary_color || '#9ca3af'
  const footerPrimaryColor = branding?.primary_color || '#0d9488'

  // Show full footer for main portals
  return (
    <footer 
      className="footer py-6"
      style={{ backgroundColor: footerBgColor }}
    >
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            {mounted ? (
              <img
                src={footerLogo}
                alt="Total Access Solutions"
                width={200}
                height={60}
                style={{ maxHeight: '60px', width: 'auto' }}
                className="mb-2"
                suppressHydrationWarning
              />
            ) : (
              <div style={{ height: '60px', width: '200px' }} className="mb-2" />
            )}
            <p style={{ color: footerTextColor }} className="mt-2 max-w-md">
              Providing South African businesses with Total Access to their data, every minute of every day. End-to-end business software solutions stored securely in the cloud.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-2" style={{ color: footerTextColor }}>Quick Links</h4>
            <ul className="space-y-2">
              <li><Link href="/" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Home</Link></li>
              <li><Link href="/about" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>About Us</Link></li>
              <li><Link href="/products" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Products</Link></li>
              <li><Link href="/case-studies" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Case Studies</Link></li>
              <li><Link href="/resources" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Resources</Link></li>
              <li><Link href="/support" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Support</Link></li>
              <li><Link href="/contact" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Contact</Link></li>
            </ul>
          </div>

          {/* Industry Solutions */}
          <div>
            <h4 className="text-lg font-semibold mb-2" style={{ color: footerTextColor }}>Industry Solutions</h4>
            <ul className="space-y-2">
              <li><Link href="/products/industries" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>All Industries</Link></li>
              <li><Link href="/products/field-services" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Field Services</Link></li>
              <li><Link href="/products/real-estate-management" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Real Estate</Link></li>
              <li><Link href="/products/digital-voting" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Digital Voting &amp; AGMs</Link></li>
              <li><Link href="/products/visitor-management" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Visitor Management</Link></li>
              <li><Link href="/products/security-services" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Security Services</Link></li>
            </ul>
          </div>

          {/* Legal & Account */}
          <div>
            <h4 className="text-lg font-semibold mb-2" style={{ color: footerTextColor }}>Legal & Account</h4>
            <ul className="space-y-2">
              <li><Link href="/legal#privacy-policy" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Privacy Policy</Link></li>
              <li><Link href="/legal#terms" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Terms of Service</Link></li>
              <li><Link href="/cookie-policy" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Cookie Policy</Link></li>
              <li><Link href="/encryption-info" className="transition-colors" style={{ color: footerTextColor }} onMouseEnter={(e) => e.currentTarget.style.color = footerPrimaryColor} onMouseLeave={(e) => e.currentTarget.style.color = footerTextColor}>Encryption Info</Link></li>
              <li className="pt-4"><Link href="/auth/login" className="font-semibold transition-colors" style={{ color: footerPrimaryColor }} onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'} onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}>Sign In →</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-600 mt-4 pt-4 text-center text-sm text-white">
          <p>&copy; {new Date().getFullYear()} Total Access Software Solutions T/A Total Access Solutions. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}
